import { defineComponent, ref, computed, onMounted, watch } from 'vue'
import { RouterLink, useRouter, useRoute } from 'vue-router'
import { useSmartBack } from '@/composables/use-smart-back'
import {
  NavBar as VanNavBar,
  Icon as VanIcon,
  Sticky as VanSticky,
  Search as VanSearch,
} from 'vant'
import { useRegionStore } from '@/stores/region-store'
import type { PictureSeries } from '@/api/types.gen'
import { getApiV1TukuPictureSeriesByRegion } from '@/api/sdk.gen'
import LotterySwitcher from '@/components/lottery-switcher'
import { useSeo, useSiteBrand } from '@/composables/use-seo'

/** 搜索按钮 */
function onSearch() {
  // 输入框变化已通过 computed 响应，这里保留以匹配 49208 交互
}

// ==================== 图库类别列表页（默认导出） ====================

export default defineComponent({
  name: 'GalleryCategoriesPage',
  setup() {
    const router = useRouter()
  const goBack = useSmartBack()
    const route = useRoute()
    const regionStore = useRegionStore()

    // 同步读取 region，确保在任何数据请求之前设置正确的彩种
    const qRegion = route.query.region
    if (qRegion === 'hk' || qRegion === 'aocai') {
      regionStore.setRegion(qRegion)
    }

    const value = ref('')
    const seriesList = ref<PictureSeries[]>([])

    // SEO：六合图库总入口（站点名取后台配置）
    const brand = useSiteBrand()
    useSeo({
      title: () => `六合图库大全_高清图纸资料图片 - ${brand.value}`,
      description: () =>
        `${brand.value}六合图库，汇集各类高清图纸与资料图片，按年份与彩色/黑白分类，更新及时。`,
      keywords: () => `六合图库,图纸大全,高清资料图片,六合彩图库,${brand.value}`,
    })

    async function fetchSeries() {
      const res = await getApiV1TukuPictureSeriesByRegion({
        path: { region: regionStore.region },
      })
      seriesList.value = res.data?.data ?? []
    }

    onMounted(() => {
      fetchSeries()
    })
    watch(
      () => regionStore.region,
      () => {
        router.replace({ query: { ...route.query, region: regionStore.region } })
        fetchSeries()
      },
    )

    const resultList = computed(() => {
      const kw = value.value.trim()
      if (!kw) return seriesList.value
      return seriesList.value.filter((s) => s.name?.includes(kw))
    })

    return () => (
      <div class="xilie-home">
        <VanSticky>
          <div class="sticky-body">
            <VanNavBar title="图库类别" onClickLeft={() => goBack()}>
              {{
                left: () => <VanIcon name="arrow-left" color="#333" size="16" />,
                right: () => <LotterySwitcher />,
              }}
            </VanNavBar>
            <div class="search-hd">
              <VanSearch
                v-model={value.value}
                placeholder="搜索"
                showAction
                shape="round"
                background="#fff"
                onSearch={onSearch}
              >
                {{
                  'left-icon': () => (
                    <div class="left-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <g clip-path="url(#xilie_clip0)">
                          <path
                            d="M9.5 17C13.6421 17 17 13.6421 17 9.5C17 5.35786 13.6421 2 9.5 2C5.35786 2 2 5.35786 2 9.5C2 13.6421 5.35786 17 9.5 17Z"
                            stroke="#999999"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                          <path
                            d="M15.2119 15.2109L18.125 18.124"
                            stroke="#999999"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <defs>
                          <clipPath id="xilie_clip0">
                            <rect width="20" height="20" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  ),
                  action: () => (
                    <button type="button" class="search-btn btn-reset block" onClick={onSearch}>
                      搜索
                    </button>
                  ),
                }}
              </VanSearch>
            </div>
          </div>
        </VanSticky>

        <div class="search-list">
          <div class="website-body clearfix">
            {resultList.value.map((item, i) => (
              <RouterLink
                key={item.id ?? i}
                class="website-item mt btn-reset"
                to={`/gallery/category/${item.id}?region=${regionStore.region}`}
              >
                <div>
                  <p class="bg">{item.name}</p>
                </div>
              </RouterLink>
            ))}
          </div>
        </div>
      </div>
    )
  },
})
